import React from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import useAppStore from '../store/useAppStore';
import './DiffPage.css';

/**
 * 记忆更新对比页面
 * 展示 Stage W 的记忆更新效果
 */
const DiffPage = () => {
  const memoryDiff = useAppStore((state) => state.memoryDiff);
  const reset = useAppStore((state) => state.reset);

  if (!memoryDiff) {
    return (
      <div className="diff-page">
        <div className="error-message">No memory update data available</div>
      </div>
    );
  }

  const {
    user_update,
    item_update,
    propagated_neighbor_update,
    all_neighbor_updates,
    total_neighbors_updated,
    item_title,
    user_id,
  } = memoryDiff;
  
  // 使用所有邻居更新，如果没有则使用单个邻居更新（向后兼容）
  const neighborUpdates = all_neighbor_updates && all_neighbor_updates.length > 0 
    ? all_neighbor_updates 
    : (propagated_neighbor_update ? [propagated_neighbor_update] : []);
  
  // 获取用户头像路径
  const baseUrl = import.meta.env.BASE_URL || '/demo/';
  const portraitPath = `${baseUrl}portrait/user${user_id}.png`;

  return (
    <div className="diff-page">
      <div className="diff-container">
        {/* Page Header */}
        <header className="diff-header">
          <div className="diff-header-content">
            <h1 className="diff-title">
              <span className="title-icon">🔄</span>
              Stage W: Collaborative Memory Update & Propagation
            </h1>
            <div className="update-badge">
              <span className="badge-icon">✨</span>
              <span className="badge-text">{total_neighbors_updated} Neighbor Nodes Updated</span>
            </div>
          </div>
        </header>

        {/* Update Summary */}
        <section className="update-summary">
          <h3>📌 Interacted Item</h3>
          <div className="item-box">
            <span className="item-title">{item_title}</span>
          </div>
        </section>

        {/* 记忆更新区域 */}
        <div className="diff-sections">
          
          {/* User Memory Update */}
          <section className="diff-section">
            <div className="section-header">
              <div className="section-title">
                <div className="section-avatar">
                  <img 
                    src={portraitPath} 
                    alt="User"
                    className="section-avatar-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <h2>User Memory Evolution</h2>
              </div>
              <div className="section-subtitle">
                User memory has been updated and enriched based on new interaction behavior
              </div>
            </div>
            
            <div className="diff-viewer-wrapper">
              <ReactDiffViewer
                oldValue={user_update.before}
                newValue={user_update.after}
                splitView={true}
                showDiffOnly={false}
                leftTitle="Before Update"
                rightTitle="After Update"
                styles={{
                  variables: {
                    light: {
                      diffViewerBackground: '#fff',
                      addedBackground: '#e6ffed',
                      addedColor: '#24292e',
                      removedBackground: '#ffeef0',
                      removedColor: '#24292e',
                      wordAddedBackground: '#acf2bd',
                      wordRemovedBackground: '#fdb8c0',
                      addedGutterBackground: '#cdffd8',
                      removedGutterBackground: '#ffdce0',
                      gutterBackground: '#f6f8fa',
                      gutterBackgroundDark: '#f3f4f6',
                      highlightBackground: '#fffbdd',
                      highlightGutterBackground: '#fff5b1',
                    },
                  },
                  line: {
                    padding: '10px 2px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                  },
                }}
              />
            </div>
          </section>

          {/* Item Memory Update */}
          <section className="diff-section">
            <div className="section-header">
              <div className="section-title">
                <span className="section-icon">📚</span>
                <h2>Item Memory Enhancement</h2>
              </div>
              <div className="section-subtitle">
                Item memory has been enhanced and updated based on user feedback
              </div>
            </div>
            
            <div className="diff-viewer-wrapper">
              <ReactDiffViewer
                oldValue={item_update.before}
                newValue={item_update.after}
                splitView={true}
                showDiffOnly={false}
                leftTitle="Before Update"
                rightTitle="After Update"
                styles={{
                  variables: {
                    light: {
                      diffViewerBackground: '#fff',
                      addedBackground: '#e6ffed',
                      addedColor: '#24292e',
                      removedBackground: '#ffeef0',
                      removedColor: '#24292e',
                      wordAddedBackground: '#acf2bd',
                      wordRemovedBackground: '#fdb8c0',
                      addedGutterBackground: '#cdffd8',
                      removedGutterBackground: '#ffdce0',
                      gutterBackground: '#f6f8fa',
                      gutterBackgroundDark: '#f3f4f6',
                      highlightBackground: '#fffbdd',
                      highlightGutterBackground: '#fff5b1',
                    },
                  },
                  line: {
                    padding: '10px 2px',
                    fontSize: '14px',
                    lineHeight: '1.6',
                  },
                }}
              />
            </div>
          </section>

          {/* Neighbor Propagation Updates */}
          {neighborUpdates.length > 0 && (
            <>
              <section className="diff-section highlight">
                <div className="section-header">
                  <div className="section-title">
                    <span className="section-icon">🔗</span>
                    <h2>Neighbor Memory Propagation ({neighborUpdates.length} Neighbors)</h2>
                  </div>
                  <div className="section-subtitle">
                    Collaborative memory propagation - Updates affect memory states of similar users/items
                  </div>
                </div>
              </section>

              {/* All Neighbor Updates */}
              {neighborUpdates.map((neighbor, idx) => (
                <section key={neighbor.neighbor_id || idx} className="diff-section neighbor-update">
                  <div className="section-header">
                    <div className="section-title">
                      <span className="neighbor-number">#{idx + 1}</span>
                      <h3>Neighbor: {neighbor.neighbor_id}</h3>
                    </div>
                    {neighbor.rationale && (
                      <div className="propagation-rationale">
                        <strong>Propagation Rationale:</strong> {neighbor.rationale}
                      </div>
                    )}
                  </div>
                  
                  <div className="diff-viewer-wrapper">
                    <ReactDiffViewer
                      oldValue={neighbor.before}
                      newValue={neighbor.after}
                      splitView={true}
                      showDiffOnly={false}
                      leftTitle="Before Propagation"
                      rightTitle="After Propagation"
                      styles={{
                        variables: {
                          light: {
                            diffViewerBackground: '#fff',
                            addedBackground: '#dbeafe',
                            addedColor: '#1e3a8a',
                            removedBackground: '#fef3c7',
                            removedColor: '#78350f',
                            wordAddedBackground: '#93c5fd',
                            wordRemovedBackground: '#fde68a',
                            addedGutterBackground: '#bfdbfe',
                            removedGutterBackground: '#fef08a',
                            gutterBackground: '#f6f8fa',
                            gutterBackgroundDark: '#f3f4f6',
                            highlightBackground: '#fffbdd',
                            highlightGutterBackground: '#fff5b1',
                          },
                        },
                        line: {
                          padding: '10px 2px',
                          fontSize: '14px',
                          lineHeight: '1.6',
                        },
                      }}
                    />
                  </div>
                </section>
              ))}
            </>
          )}

          {/* Propagation Statistics */}
          <section className="propagation-stats">
            <div className="stats-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-label">Total Updated Nodes</div>
                <div className="stat-value">{total_neighbors_updated + 2}</div>
                <div className="stat-detail">User + Item + {total_neighbors_updated} Neighbors</div>
              </div>
            </div>
            
            <div className="stats-card">
              <div className="stat-icon">🌐</div>
              <div className="stat-content">
                <div className="stat-label">Propagation Depth</div>
                <div className="stat-value">1-hop</div>
                <div className="stat-detail">First-order neighbors</div>
              </div>
            </div>
            
            <div className="stats-card">
              <div className="stat-icon">⚡</div>
              <div className="stat-content">
                <div className="stat-label">Update Strategy</div>
                <div className="stat-value">Async</div>
                <div className="stat-detail">Incremental graph update</div>
              </div>
            </div>
          </section>
        </div>

        {/* Complete Button */}
        <footer className="diff-footer">
          <button className="btn-complete" onClick={reset}>
            <span className="btn-icon">🏁</span>
            <span className="btn-text">Complete Demo, Back to Home</span>
          </button>
          
          <div className="footer-note">
            💡 Note: Memory updates have been saved to the graph and will affect future recommendations
          </div>
        </footer>
      </div>
    </div>
  );
};

export default DiffPage;

